import {
  Checkbox,
  FormControl,
  FormHelperText,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  SelectChangeEvent
} from '@mui/material';
import {useCallback, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';

interface WeekdaysFilterProps {
  selectedWeekdays: number[] | undefined;
  onWeekdaysChange: (selected: number[]) => void;
  onErrorChange: (error: boolean) => void;
}

const WeekdaysFilter: React.FC<WeekdaysFilterProps> = ({
  selectedWeekdays,
  onWeekdaysChange,
  onErrorChange
}) => {
  const [error, setError] = useState(false);
  const {t} = useTranslation();

  const weekdayLabels = [
    t('weekdayMonday'),
    t('weekdayTuesday'),
    t('weekdayWednesday'),
    t('weekdayThursday'),
    t('weekdayFriday'),
    t('weekdaySaturday'),
    t('weekdaySunday')
  ];

  const handleChange = useCallback(
    (event: SelectChangeEvent<number[]>) => {
      const value = event.target.value as number[];
      if (value.includes(0)) {
        if (selectedWeekdays && selectedWeekdays.length === 7) {
          onWeekdaysChange([]);
        } else {
          onWeekdaysChange([1, 2, 3, 4, 5, 6, 7]);
        }
      } else {
        onWeekdaysChange(value);
      }
    },
    [selectedWeekdays, onWeekdaysChange]
  );

  useEffect(() => {
    const hasError = !selectedWeekdays || selectedWeekdays.length === 0;
    setError(hasError);
    onErrorChange(hasError);
  }, [selectedWeekdays, onErrorChange]);

  return (
    <FormControl fullWidth>
      <InputLabel id="weekday-select-label" shrink>
        {t('weekdays')}
      </InputLabel>
      <Select
        labelId="weekday-select-label"
        label={t('weekdays')}
        multiple
        value={selectedWeekdays}
        onChange={handleChange}
        renderValue={(selected) =>
          (selected as number[])
            .map((value) => weekdayLabels[value - 1])
            .join(', ')
        }
      >
        <MenuItem key="all" value={0}>
          <Checkbox
            checked={selectedWeekdays && selectedWeekdays.length === 7}
          />
          <ListItemText primary={t('all_days')} />
        </MenuItem>
        {[1, 2, 3, 4, 5, 6, 7].map((day) => (
          <MenuItem key={day} value={day}>
            <Checkbox
              checked={selectedWeekdays && selectedWeekdays.includes(day)}
            />
            <ListItemText primary={weekdayLabels[day - 1]} />
          </MenuItem>
        ))}
      </Select>
      {error && (
        <FormHelperText
          sx={{
            color: 'red'
          }}
        >
          {t('validation:weekday_notSelectedError')}
        </FormHelperText>
      )}
    </FormControl>
  );
};

export default WeekdaysFilter;
